'use client';

import { useEffect, useRef } from 'react';
import { useUserProfile } from '@/contexts/UserProfileContext';
import { waitForSourcePoint } from '@/util/analytics';

function waitForCioIdentify(timeoutMs = 5000): Promise<boolean> {
  return new Promise((resolve) => {
    const start = Date.now();
    const check = () => {
      if (typeof window._cio?.identify === 'function') {
        resolve(true);
        return;
      }
      if (Date.now() - start >= timeoutMs) {
        resolve(false);
        return;
      }
      setTimeout(check, 100);
    };
    check();
  });
}

function waitForTcfApi(timeoutMs = 5000): Promise<boolean> {
  return new Promise((resolve) => {
    const start = Date.now();
    const check = () => {
      if (typeof window.__tcfapi === 'function') {
        resolve(true);
        return;
      }
      if (Date.now() - start >= timeoutMs) {
        resolve(false);
        return;
      }
      setTimeout(check, 100);
    };
    check();
  });
}

export default function CustomerIOIdentify() {
  const { userProfile } = useUserProfile();
  const identifiedIdRef = useRef<string | null>(null);
  const inFlightRef = useRef(false);

  useEffect(() => {
    if (!userProfile?.id) return;

    let cancelled = false;
    let listenerId: number | undefined;

    const identifyIfAllowed = async () => {
      if (inFlightRef.current) return;
      inFlightRef.current = true;

      try {
        await waitForSourcePoint();
        if (cancelled) return;

        const consent =
          await window['sourcepoint/utility'].isVendorAllowed('customerIO');
        if (!consent.allowed || cancelled) return;

        const ready = await waitForCioIdentify();
        if (!ready || cancelled) return;

        const id = String(userProfile.id);
        if (identifiedIdRef.current === id) return;

        const created = userProfile.membersince
          ? Math.floor(new Date(userProfile.membersince).getTime() / 1000)
          : NaN;

        window._cio?.identify?.({
          id,
          ...(userProfile.email ? { email: userProfile.email } : {}),
          ...(userProfile.firstname
            ? { first_name: userProfile.firstname }
            : {}),
          ...(userProfile.lastname ? { last_name: userProfile.lastname } : {}),
          ...(Number.isNaN(created) ? {} : { created_at: created }),
          is_pro: userProfile.ispro,
        });
        identifiedIdRef.current = id;
      } catch {
        // Sourcepoint timeout or consent check failed — do not queue PII
      } finally {
        inFlightRef.current = false;
      }
    };

    void identifyIfAllowed();

    void waitForTcfApi().then((ready) => {
      if (!ready || cancelled || typeof window.__tcfapi !== 'function') {
        return;
      }

      window.__tcfapi('addEventListener', 2, (tcData, success) => {
        if (typeof tcData?.listenerId === 'number') {
          listenerId = tcData.listenerId;
          if (cancelled) {
            window.__tcfapi?.('removeEventListener', 2, () => {}, listenerId);
            return;
          }
        }
        if (
          !success ||
          (tcData.eventStatus !== 'useractioncomplete' &&
            tcData.eventStatus !== 'tcloaded')
        ) {
          return;
        }
        void identifyIfAllowed();
      });
    });

    return () => {
      cancelled = true;
      if (typeof window.__tcfapi === 'function' && listenerId != null) {
        window.__tcfapi('removeEventListener', 2, () => {}, listenerId);
      }
    };
  }, [userProfile?.id]);

  return null;
}
